import React, { useMemo, useState } from 'react';
import Modal from '../Modal';
import SearchableDropdown from '../SearchableDropdown';
import { useAuth } from '../../context/AuthContext';
import { useInventory } from '../../context/InventoryContext';
import type { InventoryProduct, InventoryUnitCondition, InventoryUnitStatus } from '../../data/inventory';
import { composeAssetTagSerial, findUnitBySerialNumber } from '../../utils/assetTagBarcode';
import {
  addPurchaseOrderToInventory,
  receivePurchaseOrder,
  PURCHASE_ORDER_CONDITIONS,
  PURCHASE_ORDER_INSPECTIONS,
  type PurchaseOrderCondition,
  type PurchaseOrderInspection,
  type PurchaseOrderItemRow,
  type PurchaseOrderRow,
  type StockLineInput,
  type StockUnitInput,
} from '../../api/purchaseOrders';
import './PurchaseOrders.css';

/**
 * The Received flow, in four steps.
 *
 * 1. Fully or partially received.
 * 2. Partial only: which lines turned up, and how many of each.
 * 3. The shipment's own details — date, tracking number, condition, inspection result.
 * 4. Put the goods into inventory with what the product page keeps for them:
 *    serial numbers, status, condition, location, business and the rest.
 *
 * Steps 3 and 4 are separate server calls on purpose. The receipt is recorded as
 * soon as step 3 is submitted, so closing the dialog at step 4 leaves the PO
 * correctly marked received with its lines flagged as still awaiting inventory,
 * rather than losing the receipt entirely. Opening with `startAt="stock"` picks those
 * lines up later without recording the receipt again.
 *
 * A write-in line cannot be stocked without a decision: the user either points it at
 * an existing product or promotes it to a new one. Declining leaves it out of the
 * request, so nothing is stocked against a guess.
 *
 * Each line is recorded the way the product page holds that type: a robot one unit
 * at a time, a stock item as a counted row, and an accessory either way.
 */

type Step = 'choose' | 'items' | 'details' | 'stock';
type StockMode = 'serialized' | 'quantity';
type CustomTarget = 'new-product' | 'existing-product';
type CatalogType = InventoryProduct['type'];

/** In the product page's order. */
const UNIT_STATUSES: InventoryUnitStatus[] = ['Deployed', 'In Storage', 'Repair', 'Out of Order', 'Event', 'In-Route'];
const UNIT_CONDITIONS: InventoryUnitCondition[] = ['Working', 'Broken'];

function todayInput(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

interface UnitDraft {
  key: string;
  serialNumber: string;
  status: InventoryUnitStatus;
  condition: InventoryUnitCondition;
}

function blankUnit(): UnitDraft {
  return {
    key: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    serialNumber: '',
    status: 'In Storage',
    condition: 'Working',
  };
}

interface StockLineState {
  itemId: number;
  description: string;
  isCustom: boolean;
  productId: string | null;
  productName: string | null;
  /** The catalog product's type. A write-in's type follows the product chosen for it. */
  productType: CatalogType | null;
  qtyReceived: number;
  include: boolean;
  customTarget: CustomTarget;
  existingProductName: string;
  newProductName: string;
  newProductType: CatalogType;
  newProductSku: string;
  newProductCost: string;
  mode: StockMode;
  units: UnitDraft[];
  /** Shared by every unit on the line, or describing its one counted row. */
  category: string;
  countryOfOrigin: string;
  model: string;
  manufacturer: string;
  location: string;
  business: string;
  assembled: boolean;
  /** The counted row's own values. */
  quantity: string;
  status: InventoryUnitStatus;
  condition: InventoryUnitCondition;
}

/** Quantity received on this line that is not in inventory yet. */
function unstockedQuantity(item: PurchaseOrderItemRow): number {
  return Math.max(0, item.qty_received - (item.qty_added_to_inventory || 0));
}

function asCatalogType(value: string | null): CatalogType | null {
  return value === 'Robot' || value === 'Accessory' || value === 'Stock Item' ? value : null;
}

/** Builds the step-4 rows from whatever has been received and not yet stocked. */
function buildStockLines(items: PurchaseOrderItemRow[]): StockLineState[] {
  return items
    .filter((i) => unstockedQuantity(i) > 0)
    .map((i): StockLineState => {
      const count = Math.max(1, Math.round(unstockedQuantity(i)));
      const productType = i.is_custom ? null : asCatalogType(i.product_type);
      return {
        itemId: i.id,
        description: i.description,
        isCustom: i.is_custom,
        productId: i.product_id,
        productName: i.product_name,
        productType,
        qtyReceived: count,
        // A catalog line is stocked by default; a write-in needs an explicit yes.
        include: !i.is_custom,
        customTarget: 'new-product',
        existingProductName: '',
        newProductName: i.description.slice(0, 120),
        newProductType: 'Stock Item',
        newProductSku: '',
        newProductCost: '',
        mode: productType === 'Robot' ? 'serialized' : 'quantity',
        units: Array.from({ length: count }, blankUnit),
        category: '',
        countryOfOrigin: '',
        model: '',
        manufacturer: '',
        location: '',
        business: '',
        // The product page adds units as assembled unless told otherwise.
        assembled: true,
        quantity: String(count),
        status: 'In Storage',
        condition: 'Working',
      };
    });
}

/** Robots are tracked one unit at a time and stock items as counted rows; accessories go either way. */
function allowedModes(type: CatalogType | null): StockMode[] {
  if (type === 'Robot') return ['serialized'];
  if (type === 'Stock Item') return ['quantity'];
  return ['serialized', 'quantity'];
}

interface PurchaseOrderReceiveModalProps {
  isOpen: boolean;
  purchaseOrder: PurchaseOrderRow;
  items: PurchaseOrderItemRow[];
  /** `stock` skips the receipt and opens on the received lines still awaiting inventory. */
  startAt?: 'receive' | 'stock';
  onClose: () => void;
  /** Fired after the receipt and again after stocking, so the tables stay current. */
  onChanged: () => void;
}

const PurchaseOrderReceiveModal: React.FC<PurchaseOrderReceiveModalProps> = ({
  isOpen,
  purchaseOrder,
  items,
  startAt = 'receive',
  onClose,
  onChanged,
}) => {
  const { user } = useAuth();
  const { products, units: inventoryUnits, refreshProducts, refreshUnits } = useInventory();

  const [step, setStep] = useState<Step>(startAt === 'stock' ? 'stock' : 'choose');
  const [receiptType, setReceiptType] = useState<'Full' | 'Partial'>('Full');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Step 2 — cumulative received quantity per line, keyed by item id.
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [qtyByItem, setQtyByItem] = useState<Record<number, string>>({});

  // Step 3 — the tracking number and notes start from what the PO already holds,
  // so recording a second receipt does not blank them.
  const [dateReceived, setDateReceived] = useState(todayInput);
  const [carrierTracking, setCarrierTracking] = useState(purchaseOrder.carrier_tracking ?? '');
  const [condition, setCondition] = useState<PurchaseOrderCondition>('Good');
  const [inspection, setInspection] = useState<PurchaseOrderInspection>('Pass');
  const [discrepancyNotes, setDiscrepancyNotes] = useState(purchaseOrder.discrepancy_notes ?? '');

  // Step 4
  const [stockLines, setStockLines] = useState<StockLineState[]>(() =>
    startAt === 'stock' ? buildStockLines(items) : []
  );
  const [stockDone, setStockDone] = useState<string>('');

  const productOptions = useMemo(
    () =>
      products
        .filter((p) => p.catalogStatus !== 'Void')
        .map((p) => p.name)
        .sort((a, b) => a.localeCompare(b)),
    [products]
  );

  const outstanding = items.filter((i) => i.qty_outstanding > 0);

  const reset = () => {
    setStep(startAt === 'stock' ? 'stock' : 'choose');
    setReceiptType('Full');
    setError('');
    setChecked({});
    setQtyByItem({});
    setDateReceived(todayInput());
    setCarrierTracking(purchaseOrder.carrier_tracking ?? '');
    setCondition('Good');
    setInspection('Pass');
    setDiscrepancyNotes(purchaseOrder.discrepancy_notes ?? '');
    setStockLines([]);
    setStockDone('');
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const startFull = () => {
    setReceiptType('Full');
    setStep('details');
  };

  const startPartial = () => {
    setReceiptType('Partial');
    const nextChecked: Record<number, boolean> = {};
    const nextQty: Record<number, string> = {};
    for (const item of items) {
      nextChecked[item.id] = false;
      nextQty[item.id] = String(item.qty_outstanding > 0 ? item.qty_outstanding : item.qty);
    }
    setChecked(nextChecked);
    setQtyByItem(nextQty);
    setStep('items');
  };

  /** A write-in takes the type of the product it is being stocked as. */
  const lineType = (line: StockLineState): CatalogType | null => {
    if (!line.isCustom) return line.productType;
    if (line.customTarget === 'new-product') return line.newProductType;
    return products.find((p) => p.name === line.existingProductName)?.type ?? null;
  };

  const lineMode = (line: StockLineState): StockMode => {
    const modes = allowedModes(lineType(line));
    return modes.includes(line.mode) ? line.mode : modes[0];
  };

  const submitReceipt = async () => {
    setError('');
    setBusy(true);
    try {
      const payloadItems =
        receiptType === 'Partial'
          ? items
              .filter((i) => checked[i.id])
              .map((i) => ({
                id: i.id,
                // The server takes the running total for the line, not this delta.
                qty_received: Math.min(i.qty, i.qty_received + (Number(qtyByItem[i.id]) || 0)),
              }))
          : undefined;

      if (receiptType === 'Partial' && (!payloadItems || payloadItems.length === 0)) {
        setError('Check at least one item that was received.');
        setBusy(false);
        return;
      }

      const { items: updatedItems } = await receivePurchaseOrder(purchaseOrder.id, {
        user_id: user?.id ?? null,
        receipt_type: receiptType,
        date_received: dateReceived || null,
        carrier_tracking: carrierTracking.trim() || null,
        condition_on_arrival: condition,
        inspection_result: inspection,
        discrepancy_notes: discrepancyNotes.trim() || null,
        items: payloadItems,
      });
      onChanged();

      const lines = buildStockLines(updatedItems);
      if (lines.length === 0) {
        reset();
        onClose();
        return;
      }
      setStockLines(lines);
      setStep('stock');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record the receipt.');
    } finally {
      setBusy(false);
    }
  };

  const updateStockLine = (itemId: number, patch: Partial<StockLineState>) => {
    setStockLines((prev) => prev.map((l) => (l.itemId === itemId ? { ...l, ...patch } : l)));
  };

  const updateUnit = (itemId: number, key: string, patch: Partial<UnitDraft>) => {
    setStockLines((prev) =>
      prev.map((l) =>
        l.itemId === itemId ? { ...l, units: l.units.map((u) => (u.key === key ? { ...u, ...patch } : u)) } : l
      )
    );
  };

  const submitStock = async () => {
    setError('');
    const selected = stockLines.filter((l) => l.include);
    if (selected.length === 0) {
      setError('Select at least one line to add to inventory, or close this dialog to leave them for later.');
      return;
    }

    const payload: StockLineInput[] = [];
    const serialsInRequest = new Set<string>();
    for (const line of selected) {
      const entry: StockLineInput = { item_id: line.itemId };

      if (line.isCustom) {
        if (line.customTarget === 'existing-product') {
          const product = products.find((p) => p.name === line.existingProductName);
          if (!product) {
            setError(`Pick an existing inventory product for "${line.description}", or create it as a new product.`);
            return;
          }
          entry.product_id = product.id;
        } else {
          const name = line.newProductName.trim();
          if (!name) {
            setError(`Give the new product for "${line.description}" a name.`);
            return;
          }
          entry.create_product = {
            name,
            type: line.newProductType,
            sku: line.newProductSku.trim(),
            replacement_cost_per_item: line.newProductCost.trim() || null,
          };
        }
      } else if (line.productId) {
        entry.product_id = line.productId;
      } else {
        setError(`"${line.description}" has no inventory product attached.`);
        return;
      }

      const type = lineType(line);
      if (lineMode(line) === 'serialized') {
        if (line.units.length === 0) {
          setError(`Add at least one unit for "${line.description}".`);
          return;
        }
        const shared: StockUnitInput = {
          product_type: line.category.trim(),
          country_of_origin: line.countryOfOrigin.trim(),
          model: line.model.trim(),
          manufacturer: line.manufacturer.trim() || null,
          location: line.location.trim() || null,
          business: line.business.trim() || null,
          assembled: line.assembled,
        };
        const units: StockUnitInput[] = [];
        for (const unit of line.units) {
          // Saved as USA-<category>-<number> once a category is set, as the product page does.
          const serial = composeAssetTagSerial(line.countryOfOrigin, line.category, unit.serialNumber);
          if (serial) {
            if (serialsInRequest.has(serial.toLowerCase())) {
              setError(`Serial number ${serial} is entered more than once.`);
              return;
            }
            serialsInRequest.add(serial.toLowerCase());
            const existing = findUnitBySerialNumber(inventoryUnits, serial);
            if (existing) {
              const holder = products.find((p) => p.id === existing.productId)?.name;
              setError(`Serial number ${serial} is already in inventory${holder ? ` under ${holder}` : ''}.`);
              return;
            }
          }
          units.push({ ...shared, serial_number: serial, status: unit.status, condition: unit.condition });
        }
        entry.units = units;
      } else {
        const quantity = Math.round(Number(line.quantity) || 0);
        if (quantity <= 0) {
          setError(`Enter a quantity for "${line.description}".`);
          return;
        }
        entry.stock_quantity = quantity;
        entry.manufacturer = line.manufacturer.trim() || null;
        entry.location = line.location.trim() || null;
        entry.assembled = line.assembled;
        if (type === 'Stock Item') {
          // The product page keeps a stock row as deployed or not, with no condition.
          entry.status = line.status === 'Deployed' ? 'Deployed' : 'In Storage';
          entry.condition = null;
        } else {
          entry.business = line.business.trim() || null;
          entry.status = line.status;
          entry.condition = line.condition;
        }
      }
      payload.push(entry);
    }

    setBusy(true);
    try {
      const result = await addPurchaseOrderToInventory(purchaseOrder.id, payload, user?.id ?? null);
      await Promise.all([refreshProducts(), refreshUnits()]);
      onChanged();
      const created = result.productsCreated.length;
      const remaining = buildStockLines(result.items);
      setStockDone(
        `Added ${result.unitsCreated} inventory ${result.unitsCreated === 1 ? 'record' : 'records'}` +
          (created > 0 ? ` and created ${created} new ${created === 1 ? 'product' : 'products'}.` : '.') +
          (remaining.length > 0
            ? ` ${remaining.length} ${remaining.length === 1 ? 'line is' : 'lines are'} still awaiting inventory.`
            : '')
      );
      setStockLines(remaining);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add the items to inventory.');
    } finally {
      setBusy(false);
    }
  };

  const title =
    step === 'choose'
      ? `Receive ${purchaseOrder.po_number}`
      : step === 'items'
        ? 'Which items were received?'
        : step === 'details'
          ? 'Shipment details'
          : `Add ${purchaseOrder.po_number} items to inventory`;

  const renderTextField = (
    line: StockLineState,
    field: 'category' | 'countryOfOrigin' | 'model' | 'manufacturer' | 'location' | 'business',
    label: string,
    placeholder?: string
  ) => {
    const id = `po-stock-${line.itemId}-${field}`;
    return (
      <div className="form-group">
        <label className="form-label" htmlFor={id}>{label}</label>
        <input
          id={id}
          type="text"
          className="form-input"
          value={line[field]}
          onChange={(e) => updateStockLine(line.itemId, { [field]: e.target.value })}
          placeholder={placeholder}
          disabled={!line.include}
        />
      </div>
    );
  };

  const renderYesNo = (
    line: StockLineState,
    id: string,
    label: string,
    value: boolean,
    onChange: (next: boolean) => void
  ) => (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>{label}</label>
      <select
        id={id}
        className="form-input"
        value={value ? 'yes' : 'no'}
        onChange={(e) => onChange(e.target.value === 'yes')}
        disabled={!line.include}
      >
        <option value="no">No</option>
        <option value="yes">Yes</option>
      </select>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} extraWide={step === 'stock'} wide={step !== 'stock'}>
      <div className="modal-body">
        {step === 'choose' && (
          <div className="po-choice-list">
            <p className="po-choice-prompt">
              {purchaseOrder.vendor_name} · {purchaseOrder.item_count}{' '}
              {purchaseOrder.item_count === 1 ? 'line' : 'lines'} · {purchaseOrder.qty_ordered} ordered
              {purchaseOrder.qty_received > 0 ? `, ${purchaseOrder.qty_received} already received` : ''}
            </p>
            <button type="button" className="po-choice-btn" onClick={startFull}>
              <strong>Fully Received</strong>
              <span>Everything on the purchase order turned up.</span>
            </button>
            <button type="button" className="po-choice-btn" onClick={startPartial}>
              <strong>Partially Received</strong>
              <span>Pick which lines arrived and how many of each.</span>
            </button>
          </div>
        )}

        {step === 'items' && (
          <>
            <p className="po-choice-prompt">Check each line that arrived and confirm the quantity received.</p>
            <div className="po-table-wrapper">
              <table className="po-table">
                <thead>
                  <tr>
                    <th className="po-col-check">Received</th>
                    <th>Item / Description</th>
                    <th className="po-col-num">Ordered</th>
                    <th className="po-col-num">Already in</th>
                    <th className="po-col-num">Receiving now</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className={item.qty_outstanding === 0 ? 'po-row-muted' : undefined}>
                      <td className="po-col-check">
                        <input
                          type="checkbox"
                          checked={checked[item.id] === true}
                          onChange={(e) => setChecked((prev) => ({ ...prev, [item.id]: e.target.checked }))}
                          aria-label={`Received ${item.description}`}
                        />
                      </td>
                      <td>
                        {item.description}
                        {item.is_custom ? <span className="po-custom-tag">Custom</span> : null}
                      </td>
                      <td className="po-col-num">{item.qty}</td>
                      <td className="po-col-num">{item.qty_received}</td>
                      <td className="po-col-num">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          max={item.qty_outstanding}
                          className="form-input po-qty-input"
                          value={qtyByItem[item.id] ?? ''}
                          onChange={(e) => setQtyByItem((prev) => ({ ...prev, [item.id]: e.target.value }))}
                          disabled={checked[item.id] !== true}
                          aria-label={`Quantity received for ${item.description}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {outstanding.length === 0 && (
              <p className="po-form-hint">Every line is already fully received.</p>
            )}
          </>
        )}

        {step === 'details' && (
          <>
            <p className="po-choice-prompt">
              {receiptType === 'Full'
                ? 'Every line will be marked fully received.'
                : 'Only the lines you checked will be credited.'}
            </p>
            <div className="po-form-grid po-form-grid--2">
              <div className="form-group">
                <label className="form-label" htmlFor="po-date-received">Date Received</label>
                <input
                  id="po-date-received"
                  type="date"
                  className="form-input"
                  value={dateReceived}
                  onChange={(e) => setDateReceived(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-carrier">Tracking Number</label>
                <input
                  id="po-carrier"
                  type="text"
                  className="form-input"
                  value={carrierTracking}
                  onChange={(e) => setCarrierTracking(e.target.value)}
                  placeholder="Carrier and tracking #"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-condition">Condition on Arrival</label>
                <select
                  id="po-condition"
                  className="form-input"
                  value={condition}
                  onChange={(e) => setCondition(e.target.value as PurchaseOrderCondition)}
                >
                  {PURCHASE_ORDER_CONDITIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-inspection">Inspection Result</label>
                <select
                  id="po-inspection"
                  className="form-input"
                  value={inspection}
                  onChange={(e) => setInspection(e.target.value as PurchaseOrderInspection)}
                >
                  {PURCHASE_ORDER_INSPECTIONS.map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
                {inspection === 'Fail' && (
                  <span className="po-form-hint">A failed inspection flags the shipment as Rejected — Return Needed.</span>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="po-discrepancy">Discrepancy / Damage Notes</label>
              <textarea
                id="po-discrepancy"
                className="form-textarea"
                rows={3}
                value={discrepancyNotes}
                onChange={(e) => setDiscrepancyNotes(e.target.value)}
                placeholder="Shortages, damage, wrong items…"
              />
            </div>
          </>
        )}

        {step === 'stock' && (
          <>
            {stockDone ? <p className="po-success" role="status">{stockDone}</p> : null}
            {stockLines.length === 0 ? (
              <p className="po-choice-prompt">Everything received has been added to inventory.</p>
            ) : (
              <>
                <p className="po-choice-prompt">
                  Enter the details for each received line. Anything you leave unchecked stays awaiting inventory;
                  use Add to inventory on the purchase order to come back to it.
                </p>
                {stockLines.map((line) => {
                  const type = lineType(line);
                  const modes = allowedModes(type);
                  const mode = lineMode(line);
                  return (
                    <section
                      key={line.itemId}
                      className={`po-stock-line${line.include ? '' : ' po-stock-line--excluded'}`}
                    >
                      <header className="po-stock-line-head">
                        <label className="po-stock-line-toggle">
                          <input
                            type="checkbox"
                            checked={line.include}
                            onChange={(e) => updateStockLine(line.itemId, { include: e.target.checked })}
                          />
                          <span className="po-stock-line-name">{line.description}</span>
                        </label>
                        <span className="po-stock-line-meta">
                          {line.qtyReceived} received
                          {line.productName ? ` · ${line.productName}` : ''}
                          {type ? ` · ${type}` : ''}
                        </span>
                        {line.isCustom ? <span className="po-custom-tag">Custom</span> : null}
                      </header>

                      {line.isCustom && (
                        <div className="po-stock-custom">
                          <p className="po-stock-custom-warning">
                            This was written in by hand, so it is not an inventory product yet. Point it at an existing
                            product or create a new one before it can be stocked.
                          </p>
                          <div className="po-stock-custom-choice" role="group" aria-label="Custom item destination">
                            <label>
                              <input
                                type="radio"
                                name={`po-custom-${line.itemId}`}
                                checked={line.customTarget === 'new-product'}
                                onChange={() => updateStockLine(line.itemId, { customTarget: 'new-product' })}
                                disabled={!line.include}
                              />
                              Create it as a new product
                            </label>
                            <label>
                              <input
                                type="radio"
                                name={`po-custom-${line.itemId}`}
                                checked={line.customTarget === 'existing-product'}
                                onChange={() => updateStockLine(line.itemId, { customTarget: 'existing-product' })}
                                disabled={!line.include}
                              />
                              Add it to an existing product
                            </label>
                          </div>

                          {line.customTarget === 'new-product' ? (
                            <div className="po-form-grid po-form-grid--4">
                              <div className="form-group">
                                <label className="form-label">Product name</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={line.newProductName}
                                  onChange={(e) => updateStockLine(line.itemId, { newProductName: e.target.value })}
                                  disabled={!line.include}
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Type</label>
                                <select
                                  className="form-input"
                                  value={line.newProductType}
                                  onChange={(e) =>
                                    updateStockLine(line.itemId, { newProductType: e.target.value as CatalogType })
                                  }
                                  disabled={!line.include}
                                >
                                  <option value="Stock Item">Stock Item</option>
                                  <option value="Accessory">Accessory</option>
                                  <option value="Robot">Robot</option>
                                </select>
                              </div>
                              <div className="form-group">
                                <label className="form-label">SKU</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={line.newProductSku}
                                  onChange={(e) => updateStockLine(line.itemId, { newProductSku: e.target.value })}
                                  disabled={!line.include}
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Replacement cost</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={line.newProductCost}
                                  onChange={(e) => updateStockLine(line.itemId, { newProductCost: e.target.value })}
                                  placeholder="$0.00"
                                  disabled={!line.include}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="form-group">
                              <label className="form-label">Existing product</label>
                              <SearchableDropdown
                                options={productOptions}
                                value={line.existingProductName}
                                onChange={(value) => updateStockLine(line.itemId, { existingProductName: value })}
                                placeholder="Search inventory products..."
                                disabled={!line.include}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {modes.length > 1 ? (
                        <div className="po-stock-mode" role="group" aria-label="How to record this line">
                          <label>
                            <input
                              type="radio"
                              name={`po-mode-${line.itemId}`}
                              checked={mode === 'serialized'}
                              onChange={() => updateStockLine(line.itemId, { mode: 'serialized' })}
                              disabled={!line.include}
                            />
                            Record each unit (serial numbers)
                          </label>
                          <label>
                            <input
                              type="radio"
                              name={`po-mode-${line.itemId}`}
                              checked={mode === 'quantity'}
                              onChange={() => updateStockLine(line.itemId, { mode: 'quantity' })}
                              disabled={!line.include}
                            />
                            Record a count
                          </label>
                        </div>
                      ) : (
                        <p className="po-stock-mode-note">
                          {type === 'Robot'
                            ? 'Robots go into inventory one unit at a time.'
                            : 'Stock items go into inventory as a count.'}
                        </p>
                      )}

                      {mode === 'serialized' ? (
                        <>
                          <p className="po-stock-shared-note">These apply to every unit below.</p>
                          <div className="po-form-grid po-form-grid--4">
                            {renderTextField(line, 'category', 'Product type (asset tag category)', 'e.g. Garg')}
                            {renderTextField(line, 'countryOfOrigin', 'Country of origin', 'e.g. United States')}
                            {renderTextField(line, 'model', 'Model')}
                            {renderTextField(line, 'manufacturer', 'Manufacturer')}
                            {renderTextField(line, 'location', 'Location', 'City / location')}
                            {renderTextField(line, 'business', 'Business')}
                            {renderYesNo(line, `po-stock-${line.itemId}-assembled`, 'Assembled', line.assembled, (next) =>
                              updateStockLine(line.itemId, { assembled: next })
                            )}
                          </div>
                          <div className="po-table-wrapper">
                            <table className="po-table po-table--units">
                              <thead>
                                <tr>
                                  <th className="po-col-num">#</th>
                                  <th>Serial Number</th>
                                  <th className="po-col-unit-status">Status</th>
                                  <th className="po-col-unit-condition">Condition</th>
                                  <th className="po-col-unit-remove" aria-label="Remove" />
                                </tr>
                              </thead>
                              <tbody>
                                {line.units.map((unit, idx) => (
                                  <tr key={unit.key}>
                                    <td className="po-col-num">{idx + 1}</td>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-input"
                                        value={unit.serialNumber}
                                        onChange={(e) =>
                                          updateUnit(line.itemId, unit.key, { serialNumber: e.target.value })
                                        }
                                        placeholder="Optional"
                                        disabled={!line.include}
                                        aria-label={`Serial number for unit ${idx + 1}`}
                                      />
                                    </td>
                                    <td>
                                      <select
                                        className="form-input"
                                        value={unit.status}
                                        onChange={(e) =>
                                          updateUnit(line.itemId, unit.key, {
                                            status: e.target.value as InventoryUnitStatus,
                                          })
                                        }
                                        disabled={!line.include}
                                        aria-label={`Status for unit ${idx + 1}`}
                                      >
                                        {UNIT_STATUSES.map((s) => (
                                          <option key={s} value={s}>{s}</option>
                                        ))}
                                      </select>
                                    </td>
                                    <td>
                                      <select
                                        className="form-input"
                                        value={unit.condition}
                                        onChange={(e) =>
                                          updateUnit(line.itemId, unit.key, {
                                            condition: e.target.value as InventoryUnitCondition,
                                          })
                                        }
                                        disabled={!line.include}
                                        aria-label={`Condition for unit ${idx + 1}`}
                                      >
                                        {UNIT_CONDITIONS.map((c) => (
                                          <option key={c} value={c}>{c}</option>
                                        ))}
                                      </select>
                                    </td>
                                    <td>
                                      {line.units.length > 1 && (
                                        <button
                                          type="button"
                                          className="po-form-item-remove"
                                          onClick={() =>
                                            updateStockLine(line.itemId, {
                                              units: line.units.filter((u) => u.key !== unit.key),
                                            })
                                          }
                                          disabled={!line.include}
                                          aria-label={`Remove unit ${idx + 1}`}
                                        >
                                          Remove
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <button
                            type="button"
                            className="po-secondary-btn"
                            onClick={() => updateStockLine(line.itemId, { units: [...line.units, blankUnit()] })}
                            disabled={!line.include}
                          >
                            + Add unit
                          </button>
                        </>
                      ) : (
                        <div className="po-form-grid po-form-grid--4">
                          <div className="form-group">
                            <label className="form-label" htmlFor={`po-stock-${line.itemId}-quantity`}>Count</label>
                            <input
                              id={`po-stock-${line.itemId}-quantity`}
                              type="number"
                              min="1"
                              step="1"
                              className="form-input"
                              value={line.quantity}
                              onChange={(e) => updateStockLine(line.itemId, { quantity: e.target.value })}
                              disabled={!line.include}
                            />
                          </div>
                          {renderTextField(line, 'manufacturer', 'Manufacturer', 'Optional')}
                          {renderTextField(
                            line,
                            'location',
                            'Location',
                            type === 'Stock Item' ? 'Warehouse / site / shelf' : 'City / location'
                          )}
                          {type === 'Stock Item' ? (
                            renderYesNo(
                              line,
                              `po-stock-${line.itemId}-deployed`,
                              'Deployed',
                              line.status === 'Deployed',
                              (next) => updateStockLine(line.itemId, { status: next ? 'Deployed' : 'In Storage' })
                            )
                          ) : (
                            <>
                              {renderTextField(line, 'business', 'Business')}
                              <div className="form-group">
                                <label className="form-label" htmlFor={`po-stock-${line.itemId}-status`}>Status</label>
                                <select
                                  id={`po-stock-${line.itemId}-status`}
                                  className="form-input"
                                  value={line.status}
                                  onChange={(e) =>
                                    updateStockLine(line.itemId, { status: e.target.value as InventoryUnitStatus })
                                  }
                                  disabled={!line.include}
                                >
                                  {UNIT_STATUSES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label className="form-label" htmlFor={`po-stock-${line.itemId}-condition`}>
                                  Condition
                                </label>
                                <select
                                  id={`po-stock-${line.itemId}-condition`}
                                  className="form-input"
                                  value={line.condition}
                                  onChange={(e) =>
                                    updateStockLine(line.itemId, {
                                      condition: e.target.value as InventoryUnitCondition,
                                    })
                                  }
                                  disabled={!line.include}
                                >
                                  {UNIT_CONDITIONS.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </select>
                              </div>
                            </>
                          )}
                          {renderYesNo(line, `po-stock-${line.itemId}-assembled`, 'Assembled', line.assembled, (next) =>
                            updateStockLine(line.itemId, { assembled: next })
                          )}
                        </div>
                      )}
                    </section>
                  );
                })}
              </>
            )}
          </>
        )}

        {error ? <p className="create-order-error" role="alert">{error}</p> : null}
      </div>

      <div className="modal-actions">
        {step === 'choose' && (
          <button type="button" className="cancel-button" onClick={handleClose}>
            Cancel
          </button>
        )}

        {step === 'items' && (
          <>
            <button type="button" className="cancel-button" onClick={() => setStep('choose')} disabled={busy}>
              Back
            </button>
            <button
              type="button"
              className="po-primary-btn"
              onClick={() => {
                if (!items.some((i) => checked[i.id])) {
                  setError('Check at least one item that was received.');
                  return;
                }
                setError('');
                setStep('details');
              }}
            >
              Continue
            </button>
          </>
        )}

        {step === 'details' && (
          <>
            <button
              type="button"
              className="cancel-button"
              onClick={() => setStep(receiptType === 'Partial' ? 'items' : 'choose')}
              disabled={busy}
            >
              Back
            </button>
            <button type="button" className="po-primary-btn" onClick={submitReceipt} disabled={busy}>
              {busy ? 'Recording…' : 'Record receipt'}
            </button>
          </>
        )}

        {step === 'stock' && (
          <>
            <button type="button" className="cancel-button" onClick={handleClose} disabled={busy}>
              {stockLines.length === 0 ? 'Close' : 'Do this later'}
            </button>
            {stockLines.length > 0 && (
              <button type="button" className="po-primary-btn" onClick={submitStock} disabled={busy}>
                {busy ? 'Adding…' : 'Add to inventory'}
              </button>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default PurchaseOrderReceiveModal;
